import { Injectable, inject } from '@angular/core';
import { VoiceFlowContextService } from '../../../core/services/voice-actions/flows/voice-flow-context.service';
import { VoiceFlowItem } from '../../../core/services/voice-actions/flows/voice-flow.models';
import { AuthStore } from '../../../core/auth/state/auth.store';
import { Rights } from '../../../core/constants/rights.constants';
import { QUICK_TOOLS } from '../../quick-tools/quick-tools.registry';
import { QuickToolDefinition } from '../../quick-tools/quick-tool-definition';

type QueryValue = string | number | boolean;
type NavSource = 'topbar' | 'sidebar' | 'page-action';
type NavArea = NavSource;

interface NavigationItemMetadata {
  route: string;
  queryParams?: Record<string, QueryValue>;
  area: NavArea;
  aliases: string[];
  source: NavSource;
  right?: string;
  [key: string]: unknown;
}

interface NavigationFlowItem extends Omit<VoiceFlowItem, 'metadata'> {
  metadata: NavigationItemMetadata;
  queryParams?: Record<string, QueryValue>;
}

const FLOW_ID = 'global.navigation';
const FLOW_ROUTE_PATH = '*';

@Injectable({
  providedIn: 'root'
})
export class GlobalNavigationVoiceFlowService {
  private readonly flowContext = inject(VoiceFlowContextService);
  private readonly authStore = inject(AuthStore);

  private readonly allItems: NavigationFlowItem[] = [
    // Topbar entries
    this.define({
      id: 'topbar-edit',
      title: 'Edit',
      route: '/edit',
      body: 'Open the creator home.',
      metadata: {
        route: '/edit',
        area: 'topbar',
        source: 'topbar',
        aliases: ['edit', 'creator home', 'home', 'dashboard', 'new project']
      }
    }),
    this.define({
      id: 'topbar-video-gen',
      title: 'Smart Video',
      route: '/video-gen/generator',
      body: 'Open Smart Video generator.',
      metadata: {
        route: '/video-gen/generator',
        area: 'topbar',
        source: 'topbar',
        aliases: ['smart video', 'smart video generation'],
        right: Rights.VideoGen_View
      }
    }),
    this.define({
      id: 'topbar-template-studio',
      title: 'Assets Studio',
      route: '/templates',
      queryParams: { tab: 'templates', templateType: 'video' },
      metadata: {
        route: '/templates',
        queryParams: { tab: 'templates', templateType: 'video' },
        area: 'topbar',
        source: 'topbar',
        aliases: ['assets studio', 'asset studio', 'assets library', 'templates', 'blueprint library']
      }
    }),
    this.define({
      id: 'topbar-jivu-command',
      title: 'Jivu Command',
      route: '/jivu-command',
      body: 'Open cross-device Jivu Command center.',
      metadata: {
        route: '/jivu-command',
        area: 'topbar',
        source: 'topbar',
        aliases: [
          'jivu command',
          'command center',
          'device command',
          'send to desktop',
          'send to mobile',
          'send to other device',
          'send workflow to desktop',
          'send workflow to mobile',
          'send current workflow to desktop',
          'send current workflow to another device',
          'handoff workflow',
          'handoff current workflow',
          'continue workflow on desktop',
          'continue workflow on mobile',
          'continue this workflow on desktop',
          'continue this work on another device',
          'resume workflow on desktop',
          'resume this command on desktop',
          'run this workflow on desktop',
          'run current workflow on other device',
          'take this work to desktop',
          'take this work to mobile',
          'process this on desktop',
          'process this on another device',
          'move to jivu command',
          'move upload to jivu command',
          'move review to jivu command',
          'move assets to jivu command',
          'move render to jivu command',
          'move finalize to jivu command',
          'upload to desktop',
          'render on desktop',
          'finalize on desktop',
          'review on desktop',
          'assets on desktop',
          'jivu command kholo',
          'command center kholo',
          'desktop par bhejo',
          'mobile par bhejo',
          'web par bhejo',
          'dusri device par bhejo',
          'dosri device par bhejo',
          'dosri device par kaam chalao',
          'dusri device par kaam chalao',
          'is workflow ko desktop par bhejo',
          'ye workflow desktop par bhejo',
          'ye workflow desktop par jari rakho',
          'ye workflow dusri device par jari rakho',
          'is kaam ko desktop par continue karo',
          'is kaam ko dusri device par continue karo',
          'ye kaam dusri device par resume karo',
          'ye kaam desktop par resume karo',
          'yahan se kaam desktop par le jao',
          'yahan se kaam mobile par le jao',
          'ye kaam desktop par process karo',
          'ye kaam dusri machine par process karo',
          'upload desktop par bhejo',
          'processing desktop par chalao',
          'review desktop par bhejo',
          'assets desktop par bhejo',
          'render desktop par chalao',
          'finalize desktop par karo',
          'ye upload dusri device par bhejo',
          'ye review ka kaam desktop par bhejo',
          'assets se video desktop par banao',
          'is render ko desktop par chalao',
          'is finalize ko desktop par karo',
          'ye kaam desktop par chalao',
          'ye workflow mobile par bhejo',
          'jivu ko dusri machine par do'
        ],
        right: Rights.JivuCommand_Access
      }
    }),
    this.define({
      id: 'jivu-command-smart-video',
      title: 'Jivu Command Smart Video',
      route: '/jivu-command',
      queryParams: { panel: 'smart-video' },
      body: 'Open Jivu Command on the Smart Video handoff panel.',
      metadata: {
        route: '/jivu-command',
        queryParams: { panel: 'smart-video' },
        area: 'page-action',
        source: 'page-action',
        aliases: [
          'jivu command smart video',
          'smart video command',
          'smart video handoff',
          'send smart video to desktop',
          'move smart video to desktop',
          'continue smart video on desktop',
          'resume smart video on desktop',
          'run smart video on desktop',
          'send smart video workflow to desktop',
          'move smart video workflow to another device',
          'smart video desktop par bhejo',
          'smart video dusri device par bhejo',
          'smart video dosri device par chalao',
          'smart video kaam desktop par chalao',
          'smart video workflow desktop par jari rakho',
          'smart video kaam dusri machine par chalao',
          'smart video yahan se desktop par le jao',
          'jivu smart video command kholo'
        ],
        right: Rights.JivuCommand_Access
      }
    }),
    this.define({
      id: 'jivu-command-upload',
      title: 'Jivu Command Upload',
      route: '/jivu-command',
      queryParams: { panel: 'upload-render' },
      body: 'Open Jivu Command on the upload and processing handoff panel.',
      metadata: {
        route: '/jivu-command',
        queryParams: { panel: 'upload-render' },
        area: 'page-action',
        source: 'page-action',
        aliases: [
          'jivu command upload',
          'upload command',
          'processing upload command',
          'upload handoff',
          'processing handoff',
          'send upload to desktop',
          'move upload to desktop',
          'send processing to desktop',
          'move processing to desktop',
          'continue upload on desktop',
          'resume processing on desktop',
          'run processing on desktop',
          'send upload workflow to desktop',
          'upload desktop par bhejo',
          'processing desktop par chalao',
          'upload dusri device par bhejo',
          'upload dosri device par chalao',
          'upload workflow desktop par jari rakho',
          'processing kaam dusri machine par chalao',
          'is upload ko desktop par process karo',
          'is processing ko dusri device par bhejo',
          'jivu upload command kholo'
        ],
        right: Rights.JivuCommand_Access
      }
    }),
    this.define({
      id: 'jivu-command-template-use',
      title: 'Jivu Command Template Use',
      route: '/jivu-command',
      queryParams: { panel: 'template-use' },
      body: 'Open Jivu Command on the Template Use handoff panel.',
      metadata: {
        route: '/jivu-command',
        queryParams: { panel: 'template-use' },
        area: 'page-action',
        source: 'page-action',
        aliases: [
          'jivu command template use',
          'template use command',
          'template handoff',
          'send template to desktop',
          'move template to desktop',
          'continue template on desktop',
          'resume template on desktop',
          'run template render on desktop',
          'send template workflow to desktop',
          'move template workflow to another device',
          'template desktop par bhejo',
          'template dusri device par bhejo',
          'template dosri device par chalao',
          'template kaam desktop par chalao',
          'template workflow desktop par jari rakho',
          'template ka render desktop par chalao',
          'template ka kaam dusri machine par chalao',
          'jivu template command kholo'
        ],
        right: Rights.JivuCommand_Access
      }
    }),
    this.define({
      id: 'jivu-command-assets-review',
      title: 'Jivu Command Assets Review',
      route: '/jivu-command',
      queryParams: { panel: 'assets-use' },
      body: 'Open Jivu Command on the assets and review render handoff panel.',
      metadata: {
        route: '/jivu-command',
        queryParams: { panel: 'assets-use' },
        area: 'page-action',
        source: 'page-action',
        aliases: [
          'jivu command assets review',
          'assets command',
          'review command',
          'review render command',
          'asset sequence command',
          'asset sequence handoff',
          'review finalize command',
          'review regenerate command',
          'assets handoff',
          'review handoff',
          'send review to desktop',
          'move review to desktop',
          'send assets to desktop',
          'move assets to desktop',
          'send asset sequence to desktop',
          'move asset sequence to desktop',
          'continue review on desktop',
          'continue assets on desktop',
          'resume review render on desktop',
          'run asset sequence on desktop',
          'finalize review on desktop',
          'regenerate review on desktop',
          'review desktop par bhejo',
          'assets desktop par bhejo',
          'review render desktop par chalao',
          'assets se video desktop par banao',
          'review dusri device par bhejo',
          'assets dosri device par chalao',
          'asset sequence desktop par chalao',
          'assets se video dusri machine par banao',
          'review finalize desktop par karo',
          'review regenerate desktop par karo',
          'review ka popup desktop par kholo',
          'review ka kaam dusri device par jari rakho',
          'assets ka kaam desktop par jari rakho',
          'jivu review command kholo',
          'jivu assets command kholo'
        ],
        right: Rights.JivuCommand_Access
      }
    }),
    this.define({
      id: 'topbar-review',
      title: 'Review',
      route: '/review',
      body: 'Open the review pipeline.',
      metadata: {
        route: '/review',
        area: 'topbar',
        source: 'topbar',
        aliases: ['review', 'content review', 'pipeline review'],
        right: Rights.Review_View
      }
    }),
    this.define({
      id: 'topbar-social',
      title: 'Social',
      route: '/social/videos/youtube',
      body: 'Open social publishing hub.',
      metadata: {
        route: '/social/videos/youtube',
        area: 'topbar',
        source: 'topbar',
        aliases: ['social', 'social hub', 'social publish'],
        right: Rights.Social_View
      }
    }),
    this.define({
      id: 'topbar-jivu-ai',
      title: 'Jivu AI',
      route: '/jivu-ai',
      metadata: {
        route: '/jivu-ai',
        area: 'topbar',
        source: 'topbar',
        aliases: ['jivu ai', 'asset forge', 'ai creator', 'jivu brain', 'memory'],
        right: Rights.VideoGen_Manage
      }
    }),
    this.define({
      id: 'topbar-settings',
      title: 'Settings',
      route: '/me/settings/general',
      metadata: {
        route: '/me/settings/general',
        area: 'topbar',
        source: 'topbar',
        aliases: ['settings', 'settings home', 'settings suite'],
        right: Rights.Settings_View
      }
    }),
    // Sidebar entries
    this.define({
      id: 'sidebar-jobs',
      title: 'Jobs',
      route: '/post-gen/jobs',
      body: 'Open processing jobs.',
      metadata: {
        route: '/post-gen/jobs',
        area: 'sidebar',
        source: 'sidebar',
        aliases: ['jobs', 'processing jobs', 'queue'],
        right: Rights.Pipeline_View
      }
    }),

    this.define({
      id: 'sidebar-template-studio',
      title: 'Assets Studio',
      route: '/templates',
      queryParams: { tab: 'templates', templateType: 'video' },
      body: 'Open Assets Studio with the first blueprint tab selected.',
      metadata: {
        route: '/templates',
        queryParams: { tab: 'templates', templateType: 'video' },
        area: 'sidebar',
        source: 'sidebar',
        aliases: ['assets studio', 'asset studio', 'templates', 'post text templates', 'post text blueprints']
      }
    }),
    this.define({
      id: 'sidebar-template-soundtracks',
      title: 'Soundtracks',
      route: '/social/island',
      queryParams: { tab: 'audio' },
      body: 'Open Assets Studio soundtracks.',
      metadata: {
        route: '/social/island',
        queryParams: { tab: 'audio' },
        area: 'sidebar',
        source: 'sidebar',
        aliases: ['soundtrack', 'soundtracks', 'audio library', 'music', 'background music']
      }
    }),

    this.define({
      id: 'sidebar-settings-social',
      title: 'Social APIs',
      route: '/me/settings/social-apis',
      metadata: {
        route: '/me/settings/social-apis',
        area: 'sidebar',
        source: 'sidebar',
        aliases: ['social settings', 'youtube settings', 'connect channel'],
        right: Rights.Settings_View
      }
    }),
    this.define({
      id: 'sidebar-settings-general',
      title: 'General Settings',
      route: '/me/settings/general',
      metadata: {
        route: '/me/settings/general',
        area: 'sidebar',
        source: 'sidebar',
        aliases: ['general settings', 'processing settings', 'system settings'],
        right: Rights.Settings_View
      }
    }),
    this.define({
      id: 'sidebar-settings-keys',
      title: 'AI Keys',
      route: '/me/settings/ai-keys',
      metadata: {
        route: '/me/settings/ai-keys',
        area: 'sidebar',
        source: 'sidebar',
        aliases: ['ai keys', 'api keys', 'byok keys'],
        right: Rights.Settings_View
      }
    }),
    this.define({
      id: 'sidebar-review-processing',
      title: 'Review Processing',
      route: '/review',
      queryParams: { pipeline: 'Processing' },
      metadata: {
        route: '/review',
        queryParams: { pipeline: 'Processing' },
        area: 'sidebar',
        source: 'sidebar',
        aliases: ['processing review', 'review pipeline'],
        right: Rights.Review_View
      }
    }),
    this.define({
      id: 'sidebar-review-post-generation',
      title: 'Review Post Generation',
      route: '/review',
      queryParams: { pipeline: 'SocialPost' },
      metadata: {
        route: '/review',
        queryParams: { pipeline: 'SocialPost' },
        area: 'sidebar',
        source: 'sidebar',
        aliases: ['post generation', 'social posts review'],
        right: Rights.Review_View
      }
    }),
    this.define({
      id: 'sidebar-review-smart-video',
      title: 'Review Smart Video',
      route: '/review',
      queryParams: { pipeline: 'SmartVideo' },
      metadata: {
        route: '/review',
        queryParams: { pipeline: 'SmartVideo' },
        area: 'sidebar',
        source: 'sidebar',
        aliases: ['smart video', 'smart video review'],
        right: Rights.Review_View
      }
    }),

    this.define({
      id: 'sidebar-social-youtube',
      title: 'YouTube',
      route: '/social/videos/youtube',
      metadata: {
        route: '/social/videos/youtube',
        area: 'sidebar',
        source: 'sidebar',
        aliases: ['youtube', 'youtube hub', 'post to youtube'],
        right: Rights.Social_View
      }
    }),
    this.define({
      id: 'sidebar-social-tiktok',
      title: 'TikTok',
      route: '/social/videos/tiktok',
      metadata: {
        route: '/social/videos/tiktok',
        area: 'sidebar',
        source: 'sidebar',
        aliases: ['tiktok'],
        right: Rights.Social_View
      }
    }),
    this.define({
      id: 'sidebar-social-instagram',
      title: 'Instagram',
      route: '/social/videos/instagram',
      metadata: {
        route: '/social/videos/instagram',
        area: 'sidebar',
        source: 'sidebar',
        aliases: ['instagram', 'instagram platform'],
        right: Rights.Social_View
      }
    }),
    this.define({
      id: 'sidebar-social-facebook',
      title: 'Facebook',
      route: '/social/videos/facebook',
      metadata: {
        route: '/social/videos/facebook',
        area: 'sidebar',
        source: 'sidebar',
        aliases: ['facebook'],
        right: Rights.Social_View
      }
    }),
    this.define({
      id: 'sidebar-social-linkedin',
      title: 'LinkedIn',
      route: '/social/videos/linkedin',
      metadata: {
        route: '/social/videos/linkedin',
        area: 'sidebar',
        source: 'sidebar',
        aliases: ['linkedin'],
        right: Rights.Social_View
      }
    }),
    ...QUICK_TOOLS.map((tool) => this.defineQuickTool(tool))
  ];

  publishForCurrentUser(): void {
    const items = this.allAllowedItems();
    this.flowContext.register({
      flowId: FLOW_ID,
      label: 'Global Navigation',
      routePath: FLOW_ROUTE_PATH,
      updatedAt: new Date().toISOString(),
      capabilities: [
        {
          actionId: 'navigate_route',
          label: 'Open navigation target',
          description: 'Navigate to a global destination using routeId or explicit route + query params.',
          payloadShape: '{ "routeId": "optional", "route": "optional", "queryParams": "optional" }'
        }
      ],
      items: items.map((item) => ({
        ...item,
        route: item.route,
        queryParams: item.queryParams,
        metadata: item.metadata
      })),
      metadata: {
        routePath: FLOW_ROUTE_PATH,
        sourceCount: items.length
      }
    });
  }

  getAllowedItem(id: string): NavigationFlowItem | undefined {
    const normalizedId = id.trim().toLowerCase();
    return this.allAllowedItems().find((item) => item.id.toLowerCase() === normalizedId);
  }

  allAllowedItems(): NavigationFlowItem[] {
    return this.allItems.filter((item) => this.isAllowed(item));
  }

  private isAllowed(item: NavigationFlowItem): boolean {
    if (item.id === 'sidebar-settings-keys' && !this.authStore.isByokUser()) {
      return false;
    }

    return !item.metadata.right || this.authStore.hasRight()(item.metadata.right);
  }

  private define(item: NavigationFlowItem): NavigationFlowItem {
    const mergedQuery: Record<string, QueryValue> | undefined = item.queryParams && Object.keys(item.queryParams).length
      ? item.queryParams
      : undefined;

    return {
      ...item,
      queryParams: mergedQuery,
      metadata: {
        ...item.metadata,
        queryParams: item.metadata.queryParams || mergedQuery
      }
    };
  }

  private defineQuickTool(tool: QuickToolDefinition): NavigationFlowItem {
    const route = tool.media
      ? `/quick-tools/launch/${tool.id}`
      : tool.route.join('/').replace(/\/+/g, '/');
    const queryParams = !tool.media && tool.queryParams
      ? { ...tool.queryParams }
      : undefined;
    const aliases = Array.from(new Set([
      tool.label.toLowerCase(),
      `open ${tool.label.toLowerCase()}`,
      `use ${tool.label.toLowerCase()}`,
      tool.id.replace(/-/g, ' '),
      ...(tool.keywords ?? []),
    ]));

    return this.define({
      id: `quick-tool-${tool.id}`,
      title: tool.label,
      route,
      queryParams,
      body: tool.media?.prompt ?? tool.description,
      metadata: {
        route,
        queryParams,
        area: 'page-action',
        source: 'page-action',
        aliases,
        right: tool.right,
      },
    });
  }
}
